import React, { useContext } from "react";
import axios from "axios";
import { API_BASE_URL } from "@config/config";
import useBypassConfig from "@hooks/useBypassConfig";
import useContactDetails from "@hooks/useContactDetails";
import useModal from "@hooks/useModal";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext";
import { CheckoutComplimentaryContext } from "@context/CheckoutComplimentaryContext";
import AlertMessage from "@components/AlertMessage";
import Privacy from "@components/Privacy";
import Terms from "@components/Terms";
import Button from "@components/Ui/Button";
import Checkbox from "@components/Ui/Checkbox";
import Loader from "@components/Ui/Loader";
import Spinner from "@components/Ui/Spinner";
import ContactDetailsForm from "@components/V3/ContactDetailsForm";
import getCinemaGroupName from "@utils/getCinemaGroupName.js";
import "./style.scss";

const CheckoutComplimentary = ({ uuid, bookingSessionId, session = null }) => {
    const { openModalWithContent, portal } = useModal();
    const { whiteLabel } = useContext(ApplicationContext);
    const { user } = useContext(AuthContext);
    const { termsAccepted, setTermsAccepted, promoCodeMessage, success } = useContext(BookSessionContext);

    const {
        isSubmitting,
        error,
        checkoutComplementary,
    } = useContext(CheckoutComplimentaryContext);

    const { isBypassActive, promptUserForEmail, shouldShowDisplayName } = useBypassConfig();

    const {
        purchaseEmail,
        setPurchaseEmail,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        emailError,
        setEmailError,
        validateContactEmail,
        isContactDetailsValid,
    } = useContactDetails({ isBypassActive, promptUserForEmail, shouldShowDisplayName, user });

    const cinemaGroupName = getCinemaGroupName(session?.cinema_group_id);

    // Mirrors TermsDrawer: the paid path saves contact details before checkout, so the
    // complimentary path has to as well or bypass groups lose first/last name entirely.
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isBypassActive) {
            if (!validateContactEmail()) return;
            try {
                await axios.put(`${API_BASE_URL}/ticket/purchase-email/${uuid}`, {
                    purchase_email: purchaseEmail,
                    ...(shouldShowDisplayName && { first_name: firstName, last_name: lastName }),
                });
            } catch {
                setEmailError("Failed to save your details. Please try again.");
                return;
            }
        }

        checkoutComplementary(uuid);
    };

    if (!uuid || !bookingSessionId) {
        return <Loader isLoading={true} />;
    }

    return (
        <>
            <div className="checkout-comp__wrapper">
                <div className="checkout-comp__message">
                    <h2 className="checkout-comp__title">My order</h2>
                    <p className="paragraph">These tickets/vouchers are included with My {whiteLabel?.brand_name}</p>

                    {success && (
                        <div className="v3-booking__promo-code-msg v3-booking__promo-code-msg--success">
                            {promoCodeMessage}
                        </div>
                    )}
                </div>

                {(cinemaGroupName && (cinemaGroupName.name === "Reading" || cinemaGroupName.name === "United Cinemas")) && (
                    <div className="terms-drawer__wrapper">
                        <p className="terms-drawer__text terms-drawer__text--no-title">
                            Please note: You are buying {cinemaGroupName.article} {cinemaGroupName.name} Voucher. It cannot be redeemed online. To secure your seat, you&#39;ll need to present the voucher(s) at the cinema&#39;s box office.
                        </p>
                    </div>
                )}

                <div className="checkout-comp__content">
                    <form
                        onSubmit={(e) => handleSubmit(e)}
                        className="checkout-comp__form"
                >
                        {isBypassActive && (
                            <ContactDetailsForm
                                showDisplayName={shouldShowDisplayName}
                                firstName={firstName}
                                lastName={lastName}
                                purchaseEmail={purchaseEmail}
                                emailError={emailError}
                                onFirstNameChange={(e) => setFirstName(e.target.value)}
                                onLastNameChange={(e) => setLastName(e.target.value)}
                                onEmailChange={(e) => {
                                    setPurchaseEmail(e.target.value);
                                    setEmailError("");
                                }}
                                idPrefix="complimentary"
                                required={isBypassActive}
                            />
                        )}
                        <Checkbox
                            id="complimentary-checkbox"
                            buttonAlignment="left"
                            checked={termsAccepted}
                            onChange={() => setTermsAccepted(!termsAccepted)}
                        > {whiteLabel?.subscriber_group?.bypass_lets_get_started ? (
                            <>
                                I agree to the{" "}
                                <a
                                    href="#terms"
                                    className="inline-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openModalWithContent(<Terms />, "medium");
                                    }}
                                >
                                    terms and conditions
                                </a>{" "}
                                of use and the{" "}
                                <a
                                    href="#privacy-collection-notice"
                                    className="inline-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openModalWithContent(<Privacy />, "medium");
                                    }}
                                >
                                    privacy collection notice
                                </a>.
                            </>
                        ) : (
                            <>
                                I accept the{" "}
                                <a
                                    href="#terms"
                                    className="inline-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openModalWithContent(<Terms />, "medium");
                                    }}
                                >
                                    Terms &amp; Conditions
                                </a>{" "}
                                of purchase
                            </>
                        )}
                        </Checkbox>
                        <div
                            className="form__group"
                            data-tip={!termsAccepted ? "Please accept Terms & Conditions" : null}
                        >
                            <Button
                                additionalClasses="button button__primary"
                                disabled={
                                    !termsAccepted || isSubmitting || !isContactDetailsValid
                                    ? "disabled"
                                    : ""
                                }>
                                {isSubmitting
                                    ? <Spinner text="loading..."/>
                                    : "Confirm Order"
                                }
                            </Button>
                        </div>
                        <AlertMessage message={error} type="error"/>
                    </form>
                </div>
            </div>
            {portal}
        </>
    );
};

export default CheckoutComplimentary;
